/**
 * The Account page's membership column (ELARA-1014): the card, and what the member's billing
 * currently says.
 *
 * Read-only by design. The action that goes with it lives in the settings menu in the other column,
 * as an "Elara Membership" row into Stripe's hosted portal — a member looking for it looks down the list of
 * account settings first. See `use-billing-portal.ts`, which also records why that row is web-only.
 *
 * Nothing here is authoritative. Plan, status and dates come from the profile projection the Stripe
 * webhook maintains (ELARA-1013); Stripe is the record.
 */

import type { MembershipSubscription } from '@elara/domain/subscription';

import type { BadgeVariant } from '#ui/badge-variants.js';

import { formatDate } from '@elara/client-core/lib/date-utils';
import { cn } from '@elara/client-core/lib/utils';
import { getMembershipPlan, planRequiresPayment } from '@elara/domain/membership-plans';

import { Badge } from '#ui/index.js';

import { MembershipCard } from './MembershipCard.js';

/**
 * How each status reads to the MEMBER, and what colour says about it.
 *
 * Stripe's vocabulary is for Stripe. `past_due` and `unpaid` both mean "we could not take the
 * payment", and the difference between them — whether retries are still running — changes nothing
 * the member can do about it, so both say the same thing and both read as urgent.
 *
 * This deliberately diverges from the admin list's colours (`admin/src/views/users/
 * membership-status.tsx`), which grade the same statuses for an operator SCANNING for who needs
 * attention: there `past_due` is amber, because access continues while Stripe retries. To the
 * member whose card just failed it is not amber — it is the one thing on this page they need to
 * act on, and the remedy is one row away. Same data, different reader, different answer.
 *
 * Exhaustive `Record` on purpose: a status added to the enum fails the build here rather than
 * rendering a raw Stripe token to a member.
 */
const STATUS_PRESENTATION: Record<MembershipSubscription['status'], { label: string; variant: BadgeVariant }> = {
  active: { label: 'Active', variant: 'success' },
  trialing: { label: 'Free trial', variant: 'success' },
  past_due: { label: 'Payment failed', variant: 'destructive' },
  unpaid: { label: 'Payment failed', variant: 'destructive' },
  paused: { label: 'Paused', variant: 'secondary' },
  canceled: { label: 'Cancelled', variant: 'secondary' },
  none: { label: 'Not started', variant: 'secondary' },
  incomplete: { label: 'Awaiting payment', variant: 'secondary' },
  incomplete_expired: { label: 'Not started', variant: 'secondary' },
};

/**
 * What `currentPeriodEnd` means to the member, which depends entirely on what else is true.
 *
 * The same date is a renewal, a trial end or a cut-off, and calling a cut-off a renewal is the kind
 * of wrong that generates a support email. Returns `undefined` when the date is absent or says
 * nothing useful — a cancelled membership with no period left has no date worth showing.
 */
export function describeBillingPeriod(subscription: MembershipSubscription): string | undefined {
  const { status, currentPeriodEnd, cancelAtPeriodEnd } = subscription;
  const date = currentPeriodEnd ? formatDate(currentPeriodEnd) : '';
  if (!date) return;

  if (cancelAtPeriodEnd) return `Your membership ends on ${date}`;
  if (status === 'trialing') return `Your free trial ends on ${date}`;
  if (status === 'active') return `Renews on ${date}`;
  if (status === 'past_due' || status === 'unpaid') return `Payment was due on ${date}`;
  return;
}

export interface MembershipPanelProps {
  /** Lets the Account page place the column, which stacks above the menu on a narrow viewport. */
  className?: string;
  name: string;
  memberSince?: string;
  /** Absent for a member who predates the billing rollout, or an environment with no Stripe. */
  subscription?: MembershipSubscription;
}

export function MembershipPanel({ className, name, memberSince, subscription }: MembershipPanelProps) {
  // No subscription means no billing to describe. They still get the card: it is their membership
  // card, not their invoice.
  if (!subscription) {
    return (
      <div className={className}>
        <MembershipCard name={name} memberSince={memberSince} />
      </div>
    );
  }

  const planName = getMembershipPlan(subscription.plan)?.name ?? 'Membership';
  const period = describeBillingPeriod(subscription);
  const status = STATUS_PRESENTATION[subscription.status];
  const isComped = !planRequiresPayment(subscription.plan);

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <MembershipCard name={name} memberSince={memberSince} />

      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{planName}</span>
          <Badge variant={status.variant} size="sm">
            {status.label}
          </Badge>
        </div>
        {period && !isComped && <div className="text-sm text-muted-foreground">{period}</div>}
        {isComped && (
          // A comped membership has nothing to pay and gets no portal row (see `useBillingPortal`),
          // so this line is the only route it has — otherwise the column would simply stop.
          <div className="text-sm text-muted-foreground">
            Your membership is complimentary. Get in touch if you’d like to change it.
          </div>
        )}
      </div>
    </div>
  );
}
